import React from 'react';
import {Segment} from 'semantic-ui-react';
import ReactDiffViewer, {DiffMethod} from 'react-diff-viewer';
import numeral from 'numeral';

// import * as File from './files';
import * as Panel2 from './panel';
import makeComp from '../../util/profiler';
import * as CGReact from './cg/cgreact';
import * as Op from './cg/ops';

// TODO: maybe don't import functions from panels?
import * as PanelFileText from './PanelFileText';

const FILE_SIZE_LIMIT = 25 * 1024 * 1024;
const LINE_LENGTH_LIMIT = 5000;
const TOTAL_LINES_LIMIT = 50000;

const inputType = {
  type: 'union' as const,
  members: Object.keys(PanelFileText.EXTENSION_INFO).map(ext => ({
    type: 'array' as const,
    objectType: {
      type: 'file' as const,
      extension: ext,
    },
  })),
};

type PanelFileTextCompareProps = Panel2.PanelProps<typeof inputType>;

const PanelFileTextCompareViewer: React.FC<{
  fileContents: Array<{extension: string; text: string}>;
}> = makeComp(
  props => {
    const processedResults = React.useMemo(() => {
      return props.fileContents.map(content =>
        PanelFileText.processTextForDisplay(content.extension, content.text)
      );
    }, [props.fileContents]);

    if (processedResults == null) {
      return <div></div>;
    }

    const truncatedTotalLines = processedResults.some(
      pr => pr.truncatedTotalLines
    );
    const truncatedLineLength = processedResults.some(
      pr => pr.truncatedLineLength
    );
    const data = processedResults.map(pr => pr.text);

    const highlightSyntax = (str: string) => (
      <pre
        style={{display: 'inline'}}
        dangerouslySetInnerHTML={{
          __html: window.Prism.highlight(
            str || '',
            window.Prism.languages.python
          ),
        }}
      />
    );

    return (
      <div>
        {truncatedLineLength && (
          <Segment textAlign="center">
            Warning: some lines truncated to {LINE_LENGTH_LIMIT} characters for
            display
          </Segment>
        )}
        {truncatedTotalLines && (
          <Segment textAlign="center">
            Warning: truncated to {TOTAL_LINES_LIMIT} lines for display
          </Segment>
        )}
        {(truncatedLineLength || truncatedTotalLines) &&
          props.fileContents[0].text !== props.fileContents[1].text && (
            <Segment textAlign="center">
              Warning: Files differ but we truncated the content prior to
              diffing. Diff display may not show all mismatches.
            </Segment>
          )}
        <ReactDiffViewer
          oldValue={data[0] ?? undefined}
          newValue={data[1] ?? undefined}
          renderContent={highlightSyntax}
          compareMethod={DiffMethod.WORDS}
          styles={{
            contentText: {
              overflow: 'hidden',
            },
          }}
        />
      </div>
    );
  },
  {id: 'PanelFileTextCompareViewer'}
);

const PanelFileTextCompareContents: React.FC<PanelFileTextCompareProps> = makeComp(
  props => {
    const fileContentsNode = Op.applyOpToOneOrMany(
      Op.opFileContents,
      'file',
      props.input.path,
      {}
    );
    const fileContents = CGReact.useNodeValue(fileContentsNode);
    const files = CGReact.useNodeValue(props.input.path);
    console.log(files);
    if (fileContents.loading || files.loading) {
      return <div></div>;
    } else {
      return (
        <PanelFileTextCompareViewer
          fileContents={(fileContents.result as string[]).map(c => {
            return {
              extension: props.input.path.type.objectType.extension,
              text: c,
            };
          })}
        />
      );
    }
  },
  {id: 'PanelFileTextCompareContents'}
);

const PanelFileTextCompareSizeGuard: React.FC<{sizes: number[]}> = makeComp(
  props => {
    const largeFiles = props.sizes.filter(
      size => (size ?? 0) > FILE_SIZE_LIMIT
    );
    if (largeFiles.length > 0) {
      return (
        <Segment textAlign="center">
          Text view limited to files less than{' '}
          {numeral(FILE_SIZE_LIMIT).format('0.0b')}
        </Segment>
      );
    } else {
      return <>{props.children}</>;
    }
  },
  {id: 'PanelFileTextCompareSizeGuard'}
);

export const PanelFileTextCompare: React.FC<PanelFileTextCompareProps> = makeComp(
  props => {
    const filesSizesNode = Op.applyOpToOneOrMany(
      Op.opFileSize,
      'file',
      props.input.path,
      {}
    );
    const filesSizes = CGReact.useNodeValue(filesSizesNode);

    if (filesSizes.loading) {
      return <div></div>;
    } else {
      return (
        <PanelFileTextCompareSizeGuard sizes={filesSizes.result}>
          <PanelFileTextCompareContents {...props} />
        </PanelFileTextCompareSizeGuard>
      );
    }
  },
  {id: 'PanelFileTextCompare'}
);

export const Spec: Panel2.PanelSpec = {
  id: 'textdiff',
  displayName: 'File Diff',
  Component: PanelFileTextCompare,
  inputType,
};
